import { FormlySelectOption } from '@ngx-formly/core/select';
import { KeyPersonnelRelationshipToApplicants } from '@portal/data-access';
import { PortalFieldConfig } from '@portal/form/data-access';
import { cloneDeep } from 'lodash-es';
import { distinctUntilChanged, startWith, tap } from 'rxjs';
import { PersonnelAppointment } from '@core/data-access';
import { AddressModel, AddressSubType, AddressSubTypeLowercase, AddressType } from '@core/address';
import { AddressMetadataModel, PersonnelAddressModel } from '../model';

export function constraintRelationshipOptions(args: { field: PortalFieldConfig }) {
  const { field } = args;

  const control = field.formControl;
  const singleRelationships: string[] = KeyPersonnelRelationshipToApplicants.filter(v =>
    v.constrain.some(constraint => constraint['single'] === true),
  ).map(v => v.value);

  return control?.valueChanges.pipe(
    startWith(control?.value),
    tap(option => {
      if (!field.props || !Array.isArray(option) || !option) return;
      //reset trước
      if (option.length === 0) {
        const newOption = Array.from(KeyPersonnelRelationshipToApplicants).map(v => {
          v.disabled = false;
          return v;
        });

        field.props.options = cloneDeep(newOption);
      }

      if (option.length > 0) {
        const isSingle = singleRelationships.includes(option[0]);

        const newOption = Array.from(KeyPersonnelRelationshipToApplicants).map(v => {
          // Clone the object to avoid directly modifying the original
          const newObj = { ...v, disabled: v.disabled };

          if (isSingle) {
            // Disable all items except the selected one if `isSingle` is true
            option.forEach(o => {
              newObj.disabled = o !== newObj.value;
            });
          } else {
            // If `isSingle` is false, disable single items except for the selected one
            newObj.disabled = singleRelationships.includes(newObj.value);
          }
          return newObj;
        });

        field.props.options = cloneDeep(newOption);
      }
    }),
  );
}

export const shouldDisableAppointmentOption = (values: PersonnelAppointment[], option: FormlySelectOption): boolean => {
  const alwaysEnableOptions: Set<string> = new Set([PersonnelAppointment.AUTHORISED_SIGNATORY]);
  const optionsA: Set<string> = new Set([
    PersonnelAppointment.KEYMAN,
    PersonnelAppointment.SHAREHOLDER,
    PersonnelAppointment.DIRECTOR,
    PersonnelAppointment.GUARANTOR,
  ]);
  const optionsB: Set<string> = new Set([PersonnelAppointment.SECRETARY]);
  const selectedValues: Set<string> = new Set(values);

  // Always enable the "AUTHORISED_SIGNATORY" option
  if (alwaysEnableOptions.has(option.value)) {
    return false; // Not disabled
  }

  // Check if SECRETARY is selected, disable optionsA (KEYMAN, SHAREHOLDER, DIRECTOR, GUARANTOR)
  if (selectedValues.has(PersonnelAppointment.SECRETARY)) {
    if (optionsA.has(option.value)) {
      return true; // Disable options in optionsA when SECRETARY is selected
    }
  }

  // Check if any of optionsA (KEYMAN, SHAREHOLDER, DIRECTOR, GUARANTOR) are selected, disable SECRETARY
  if (Array.from(optionsA).some(opt => selectedValues.has(opt))) {
    if (optionsB.has(option.value)) {
      return true; // Disable SECRETARY when any option in optionsA is selected
    }
  }

  return false; // By default, option is not disabled
};

export function constraintAppointmentOptions(args: { field: PortalFieldConfig }) {
  const { field } = args;
  const control = field.formControl;

  return control?.valueChanges.pipe(
    startWith(control?.value),
    distinctUntilChanged(),
    tap((values: PersonnelAppointment[]) => {
      // console.log(values);
      if (!field.props) return;

      if (Array.isArray(values) && Array.isArray(field.props.options)) {
        field.props.options = field.props.options.map(option => {
          option.disabled = shouldDisableAppointmentOption(values, option);
          return option;
        });
      }

      // console.log({ options: field.props.options });
    }),
  );
}

export const parseAddressesToPersonnelAddress = (personnel: {
  addresses: Partial<InstanceType<typeof AddressModel>>[];
  mailingAddressDifferent: boolean;
}): PersonnelAddressModel => {
  const { addresses, mailingAddressDifferent } = personnel;

  const residentialLocal = addresses.find(v => v?.type === AddressType.RESIDENTIAL_ADDRESS && v?.subType === AddressSubType.LOCAL);
  const residentialForeign = addresses.find(v => v?.type === AddressType.RESIDENTIAL_ADDRESS && v?.subType === AddressSubType.FOREIGN);
  const mailing = addresses.find(address => address?.type === AddressType.MAILING_ADDRESS);
  const differentResidential = mailingAddressDifferent || false;

  // Prefer local address if it has any meaningful value
  const hasLocalValues =
    residentialLocal &&
    !!(
      residentialLocal.streetName?.trim() ||
      residentialLocal.postalCode?.trim() ||
      residentialLocal.blockNumber?.trim() ||
      residentialLocal.unitNumber?.trim()
    );

  let residential: Partial<InstanceType<typeof AddressModel>> = hasLocalValues
    ? residentialLocal
    : residentialForeign
    ? residentialForeign
    : { type: AddressType.RESIDENTIAL_ADDRESS, subType: AddressSubType.LOCAL };

  const personnelAddress: Partial<InstanceType<typeof PersonnelAddressModel>> = {
    residential: AddressModel.fromJson(residential),
    mailing: AddressModel.fromJson(differentResidential ? mailing : {}),
    differentResidential,
  };

  return PersonnelAddressModel.fromJson(personnelAddress);
};

export const parsePersonnelAddressToAddresses = (address?: PersonnelAddressModel) => {
  const addressMetadata: InstanceType<typeof AddressMetadataModel> = {
    haveForeignAddress: address?.residential?.subType === AddressSubType.FOREIGN,
    showForeignAddress: true, // Always set to true to distinguish between a Business Address and a Personal Address
    maillingAddressType: address?.mailing?.subType?.toLowerCase() as AddressSubTypeLowercase,
    maillingAddressDifferent: address?.differentResidential,
  };

  const addresses: AddressModel[] = [
    AddressModel.fromJson({ ...address?.residential, id: null, type: AddressType.RESIDENTIAL_ADDRESS }),
    AddressModel.fromJson({
      ...(address?.differentResidential ? address?.mailing : address?.residential),
      id: null,
      type: AddressType.MAILING_ADDRESS,
    }),
  ];

  addresses.forEach((address: AddressModel) => {
    if (address.subType === AddressSubType.LOCAL) {
      address.addressLine1 = '';
      address.addressLine2 = '';
      address.countryId = undefined;
      address.country = undefined;
    }

    if (address.subType === AddressSubType.FOREIGN) {
      address.blockNumber = '';
      address.buildingName = '';
      address.storey = '';
      address.streetName = '';
      address.unitNumber = '';
    }
  });

  return { addressMetadata, addresses };
};
